import type React from 'react';
import type { ValueType } from 'react-select/lib/types';

import * as styles from './styles';
import { v4 as uuidv4 } from 'uuid';

import type { UUID } from '@cobliteam/common-types';

import type { MaintenanceOption } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  DefaultInput,
  DescriptionListMedium,
  type ISelectOptionType,
  NumberInput,
  Select,
} from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import { AddRounded, DeleteRounded } from '@material-ui/icons';

interface Item {
  id: UUID;
  name?: MaintenanceOption;
  price?: number;
  remark?: string;
}

export interface InputItems {
  [id: UUID]: Item;
}

export interface ItemsInputsProps {
  maintenanceItemOptions: MaintenanceOption[];
  items: InputItems;
  onItemsChange: (newItems: InputItems) => void;
}

export const ItemsInputs: React.FC<ItemsInputsProps> = ({
  maintenanceItemOptions,
  items,
  onItemsChange,
}) => {
  const createNewItem = (): Item => ({ id: uuidv4() });

  const handleItemAdd = () => {
    Analytics.track('Maintenance New Item Button Clicked');
    const newItem = createNewItem();
    onItemsChange({ ...items, [newItem.id]: { ...newItem } });
  };

  const handleItemDelete = (deleteItem: Item) => {
    Analytics.track('Maintenance Delete Item Button Clicked');

    const { [deleteItem.id]: _, ...otherItems } = items;
    onItemsChange({ ...otherItems });
  };

  const handleItemOptionChange = (
    item: Item,
    selected: ValueType<ISelectOptionType>
  ) => {
    const option = selected as ISelectOptionType;
    const itemName = { key: option.value, value: option.label || '' };
    onItemsChange({ ...items, [item.id]: { ...item, name: itemName } });
  };

  const handlePriceChange = (item: Item, newValue?: number) => {
    onItemsChange({ ...items, [item.id]: { ...item, price: newValue } });
  };

  const handleObservationChange = (item: Item, observation?: string) => {
    onItemsChange({ ...items, [item.id]: { ...item, remark: observation } });
  };

  const renderItem = (item: Item, idx: number) => (
    <div key={idx} className={styles.itemWrapper}>
      <div className={styles.itemContainer}>
        <div className={styles.itemInput}>
          {idx === 0 && (
            <DescriptionListMedium id='item'>Item</DescriptionListMedium>
          )}
          <DescriptionListMedium className={styles.counterIndex}>
            <Select
              placeholder='Ex.: Filtro de óleo'
              value={
                item.name && {
                  label: item.name.value,
                  value: item.name.key,
                }
              }
              onChange={(selected) => handleItemOptionChange(item, selected)}
              maxMenuHeight={100}
              className={styles.itemSelect}
              options={maintenanceItemOptions.map(({ key, value }) => ({
                label: value,
                value: key,
              }))}
            />
          </DescriptionListMedium>
        </div>

        <div className={styles.itemInput}>
          {idx === 0 && (
            <DescriptionListMedium id='meta'>
              Custo do item
            </DescriptionListMedium>
          )}
          <NumberInput
            placeholder='R$'
            value={item.price}
            precision={2}
            disabled={!item.name}
            onNumberChange={(value) => handlePriceChange(item, value)}
          />
        </div>

        <div className={styles.itemInput}>
          {idx === 0 && (
            <DescriptionListMedium id='meta'>Observação</DescriptionListMedium>
          )}
          <div className={styles.itemWrapper}>
            <DefaultInput
              placeholder='Observações'
              value={item.remark}
              className={styles.observation}
              disabled={!item.name}
              onChange={(event) =>
                handleObservationChange(item, event.target.value)
              }
            />
            <DeleteRounded
              className={styles.deleteButton}
              onClick={() => handleItemDelete(item)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {Object.values(items).map(renderItem)}

      <Button
        variant='text'
        size='condensed'
        startIcon={<AddRounded />}
        onClick={handleItemAdd}
      >
        Adicionar itens
      </Button>
    </div>
  );
};
